/**
 * atomberg-moengage-datalayer.js
 * ─────────────────────────────────
 * Centralized MoEngage event layer — all AtombergMoe.track() calls live here.
 * Replaces atomberg-cart-analytics.js (remove that script from theme.liquid).
 *
 * Events fired here:
 *   view_product · view_category ·
 *   add_to_cart · remove_from_cart · view_cart · buy_now · begin_checkout ·
 *   check_delivery_time · searched · scan_opened · scan_succeeded ·
 *   store_locator_searched · page_not_found
 *
 * Warranty Registration tracking plan (atomberg-warranty-wizard.js /
 * atomberg-support-repeatable.js, via atomberg:warranty-event):
 * Warranty_mobile_number_entered · Clicked_Add_Another_Product ·
 * entered_serial_number · entered_model_name · Clicked_Continue_Serial_Number ·
 * Uploaded_Invoice · Entered_Purchase_Date · Entered_Source_of_Purchase ·
 * Click_continue_Purchase_details · Entered_Name · Entered_Pincode ·
 * Clicked_Continue_Your_Details · View_Warranty_Review_Page ·
 * Warranty_Registration_Successful. (view_warranty_page is NOT part of
 * this pipe — it's a GTM-only dispatch from the same wizard file, see
 * atomberg-gtm-datalayer.js; MoEngage's own view_warranty_page comes from
 * the handle-based atomberg:page-view dispatch below instead.)
 *
 * Magic Checkout events bridged from atomberg-magic-checkout-analytics.js
 * (watchMagicEvents): otp_submitted · coupon_applied · coupon_failed ·
 * payment_failure · checkout_details_submitted · Checkout_User_Info_Submitted ·
 * payment_initiated, built from magic:<mx-analytics event name> CustomEvents.
 * payment_successful is built from magic:complete
 * (window.onCompleteMagiCheckout). magic:user_data is identity-merge only
 * (setUser, no tracked event) — see that listener for why.
 *
 * Identity merges (setUser, no tracked event) also happen on
 * atomberg:support-otp-changed (Support/Warranty OTP verified) and
 * atomberg:warranty-otp-skipped (Warranty's skip-verification path) — both
 * skipped for already-logged-in Shopify customers.
 *
 * Requires: atomberg-moengage.js loaded first (exposes window.AtombergMoe).
 * Data source: #atomberg-gtm-page-data rendered by atomberg-gtm-page-data.liquid.
 */
(function () {
  'use strict';

  /** @type {any} */
  var w = window;
  if (w._atombergMoeDatalayerInit) return;
  w._atombergMoeDatalayerInit = true;

  // ── Page context ──────────────────────────────────────────────────────────
  /**
   * @type {{
   *   template: string,
   *   page: string,
   *   is_logged_in: boolean,
   *   customer: {id: number, email: string, phone: string, first_name: string, last_name: string} | null,
   *   product: {item_id: string, item_name: string, item_brand: string, item_category: string, item_variant: string, price: number, retail_price: number, discount: number, currency: string, quantity: number} | null
   * }}
   */
  var PAGE_DATA = (function () {
    var el = document.getElementById('atomberg-gtm-page-data');
    if (!el) return { template: '', page: '', is_logged_in: false, customer: null, product: null };
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (_) {
      return { template: '', page: '', is_logged_in: false, customer: null, product: null };
    }
  })();

  /** @returns {any} */
  function moe() {
    return w.AtombergMoe || null;
  }

  /**
   * Run tracking work off the interaction's critical path — the MoEngage SDK
   * does synchronous work per track() call, which must never sit between a
   * tap and its next paint (INP). Not used for events racing a navigation.
   * @param {() => void} fn
   */
  function deferTrack(fn) {
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(fn, { timeout: 2000 });
    } else {
      setTimeout(fn, 0);
    }
  }

  // ── Cart infrastructure ───────────────────────────────────────────────────
  function fetchCart() {
    var shared = w.AtombergCartState;
    if (shared) return shared.getCart({ fresh: true });
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  /**
   * @param {any} item - a /cart.js line item
   * @returns {Record<string, any>}
   */
  function normalize(item) {
    var colour = '';
    (item.options_with_values || []).forEach(function (/** @type {any} */ o) {
      var name = String(o.name || '').toLowerCase();
      if (name === 'color' || name === 'colour') colour = o.value;
    });
    return {
      variant_id: item.variant_id,
      category_name: item.product_type || '',
      product_name: item.product_title || item.title || '',
      product_colour: colour,
      product_id: item.sku || '',
      image_url: item.image || '',
      quantity: item.quantity || 0,
      // /cart.js prices are in paise.
      price: (item.final_price || 0) / 100,
      regular_price: (item.original_price || item.final_price || 0) / 100,
    };
  }

  /**
   * @param {any} cart
   * @returns {Record<string, {qty: number, item: Record<string, any>}>}
   */
  function snapshot(cart) {
    /** @type {Record<string, {qty: number, item: Record<string, any>}>} */
    var map = {};
    (cart.items || []).forEach(function (/** @type {any} */ item) {
      var n = normalize(item);
      map[String(item.variant_id)] = { qty: n.quantity, item: n };
    });
    return map;
  }

  function isoDate() {
    var d = new Date();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return d.getFullYear() + '-' + m + '-' + day;
  }

  /**
   * @param {any} cart
   * @param {string[]} keys
   * @returns {Record<string, any[]>}
   */
  function arraysPayload(cart, keys) {
    /** @type {Record<string, any[]>} */
    var out = {};
    keys.forEach(function (k) {
      out[k] = [];
    });
    (cart.items || []).forEach(function (/** @type {any} */ item) {
      var n = normalize(item);
      keys.forEach(function (k) {
        var arr = out[k];
        if (arr) arr.push(n[k]);
      });
    });
    return out;
  }

  // ── view_product ──────────────────────────────────────────────────────────
  // On PDP, option labels are read from the picker DOM once it's ready.
  // index → normalized option name (e.g. 'colour', 'sweep', 'type').
  /** @type {string[]} */
  var optionNames = [];

  function initOptionNames() {
    var picker = document.querySelector('[data-atomberg-pdp-variant-picker]');
    if (!picker) return;
    picker.querySelectorAll('[data-option-index]').forEach(function (group) {
      var idx = parseInt(group.getAttribute('data-option-index') || '0', 10);
      var labelEl = group.querySelector('.atomberg-color-tiles__label-key, .atomberg-chips__label');
      optionNames[idx] = labelEl
        ? (labelEl.textContent || '').replace(':', '').trim().toLowerCase()
        : '';
    });
  }

  /**
   * @param {string[]} opts - [option1, option2, option3] values
   * @returns {string}
   */
  function colourFromOpts(opts) {
    for (var i = 0; i < optionNames.length; i++) {
      if (/colou?r/.test(optionNames[i] || '')) return opts[i] || '';
    }
    return '';
  }

  function fireViewProduct(/** @type {Record<string, any>} */ payload) {
    var m = moe();
    if (!m) return;
    m.track('view_product', payload);
  }

  // ── view_category ─────────────────────────────────────────────────────────
  function fireViewCategory() {
    var m = moe();
    if (!m) return;
    /** @type {HTMLElement | null} */
    var heading = document.querySelector('.atomberg-ch__title');
    var categoryName = heading ? (heading.textContent || '').trim() : document.title;
    m.track('view_category', {
      category_name: categoryName,
      sub_category_name: categoryName,
    });
  }

  // ── add_to_cart / remove_from_cart via cart diff ──────────────────────────
  /**
   * @param {Record<string, {qty: number, item: any}>} prev
   * @param {Record<string, {qty: number, item: any}>} next
   */
  function diffAndFire(prev, next) {
    var m = moe();
    if (!m) return;

    Object.keys(next).forEach(function (vid) {
      var nextEntry = next[vid];
      if (!nextEntry) return;
      var prevEntry = prev[vid];
      var prevQty = prevEntry ? prevEntry.qty : 0;
      var delta = nextEntry.qty - prevQty;
      if (delta <= 0) return;
      var it = nextEntry.item;
      m.track('add_to_cart', {
        category_name: it.category_name,
        product_name: it.product_name,
        product_colour: it.product_colour,
        product_id: it.product_id,
        image_url: it.image_url,
        product_quantity: delta,
        price: it.price,
      });
    });

    Object.keys(prev).forEach(function (vid) {
      var prevEntry = prev[vid];
      if (!prevEntry) return;
      var nextEntry = next[vid];
      var nextQty = nextEntry ? nextEntry.qty : 0;
      var delta = prevEntry.qty - nextQty;
      if (delta <= 0) return;
      var it = prevEntry.item;
      m.track('remove_from_cart', {
        category_name: it.category_name,
        product_name: it.product_name,
        product_colour: it.product_colour,
        product_id: it.product_id,
        date: isoDate(),
        quantity: delta,
        price: it.price,
        image_url: it.image_url,
      });
    });
  }

  // ── view_cart ─────────────────────────────────────────────────────────────
  function fireViewCart() {
    var m = moe();
    if (!m) return;
    fetchCart()
      .then(function (/** @type {any} */ cart) {
        if (!cart.items || !cart.items.length) return;
        var p = arraysPayload(cart, [
          'category_name',
          'product_name',
          'quantity',
          'product_colour',
          'price',
          'product_id',
          'image_url',
        ]);
        m.track('view_cart', {
          category_name: p.category_name,
          product_name: p.product_name,
          product_quantity: p.quantity,
          product_colour: p.product_colour,
          price: p.price,
          product_id: p.product_id,
          image_url: p.image_url,
        });
      })
      .catch(function () {});
  }

  // ── UTM reader ────────────────────────────────────────────────────────────
  function readUtms() {
    /** @param {string} k */
    function ls(k) {
      try {
        return localStorage.getItem(k) || '';
      } catch (_) {
        return '';
      }
    }
    return {
      utm_source: ls('lt_utm_source'),
      utm_medium: ls('lt_utm_medium'),
      utm_campaign: ls('lt_utm_campaign'),
      gclid: ls('lt_gclid'),
      utm_term: ls('lt_utm_term'),
      utm_content: ls('lt_utm_content'),
    };
  }

  // ── Cart cache — populated at begin_checkout, used by later checkout events ─
  // Razorpay fires payment/address events without product data. Caching here
  // lets us enrich payment_successful / payment_failure / checkout_details_submitted.
  /** @type {Record<string, any>} */
  var cartCache = {};

  // ── buy_now + begin_checkout ──────────────────────────────────────────────
  function fireCheckoutEvents() {
    var m = moe();
    if (!m) return;
    fetchCart()
      .then(function (/** @type {any} */ cart) {
        if (!cart.items || !cart.items.length) return;
        var p = arraysPayload(cart, [
          'category_name',
          'product_name',
          'quantity',
          'product_colour',
          'price',
          'regular_price',
          'product_id',
          'image_url',
        ]);
        var coupon = (cart.cart_level_discount_applications || []).length
          ? (cart.cart_level_discount_applications[0] || {}).title || ''
          : '';

        // Populate cache so address/payment/success events have product arrays.
        cartCache = {
          category_name: p.category_name,
          product_name: p.product_name,
          quantity: p.quantity,
          product_colour: p.product_colour,
          price: p.price,
          regular_price: p.regular_price,
          product_id: p.product_id,
          image_url: p.image_url,
          total_price: (cart.total_price || 0) / 100,
          coupon_code: coupon,
        };

        var utms = readUtms();
        m.track('buy_now', {
          category_name: p.category_name,
          product_name: p.product_name,
          product_quantity: p.quantity,
          sale_price: p.price,
          regular_price: p.regular_price,
          product_id: p.product_id,
        });
        m.track(
          'begin_checkout',
          Object.assign(
            {
              category_name: p.category_name,
              product_name: p.product_name,
              quantity: p.quantity,
              product_colour: p.product_colour,
              product_id: p.product_id,
              price: p.price,
              total_price: (cart.total_price || 0) / 100,
              image_url: p.image_url,
              checkout_page: 'magic_checkout',
            },
            utms
          )
        );
      })
      .catch(function () {});
  }

  // ── Cart drawer watch ─────────────────────────────────────────────────────
  function watchDrawer() {
    var dialog = document.querySelector('cart-drawer-component dialog');
    if (!dialog) return;
    var obs = new MutationObserver(function () {
      if (/** @type {HTMLDialogElement} */ (dialog).open) fireViewCart();
    });
    obs.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  function onCartPage() {
    return /\/cart(\/|$|\?)/.test(w.location.pathname);
  }

  // ── Cart diff init ────────────────────────────────────────────────────────
  function initCartDiff() {
    /** @type {Record<string, {qty: number, item: any}> | null} */
    var baseline = null;
    // Serialize fetches so baseline mutations never interleave.
    var lock = fetchCart()
      .then(function (/** @type {any} */ cart) {
        baseline = snapshot(cart);
      })
      .catch(function () {
        baseline = {};
      });

    document.addEventListener('cart:update', function () {
      lock = lock.then(function () {
        return fetchCart()
          .then(function (/** @type {any} */ cart) {
            var next = snapshot(cart);
            if (baseline) diffAndFire(baseline, next);
            baseline = next;
          })
          .catch(function () {});
      });
    });
  }

  // ── atomberg:pdp-variant-change → view_product ────────────────────────────
  document.addEventListener('atomberg:pdp-variant-change', function (/** @type {any} */ e) {
    if (PAGE_DATA.template !== 'product' || !PAGE_DATA.product) return;
    var product = PAGE_DATA.product;
    var variant = (e.detail || {}).variant;
    if (!variant) return;
    deferTrack(function () {
      var opts = [variant.option1 || '', variant.option2 || '', variant.option3 || ''];
      fireViewProduct({
        category_name: product.item_category,
        product_name: product.item_name,
        price: (variant.price || 0) / 100,
        stock_status: variant.available ? 'IN_STOCK' : 'OUT_OF_STOCK',
        product_colour: colourFromOpts(opts),
        product_id: variant.sku || String(variant.id),
        image_url:
          variant.featured_image && variant.featured_image.src ? variant.featured_image.src : '',
      });
    });
  });

  // ── atomberg:view-product → view_product (initial PDP load) ─────────────────
  // atomberg-analytics-pdp block dispatches this with the Liquid-rendered payload
  // (includes colour, image, stock from server — richer than PAGE_DATA alone).
  document.addEventListener('atomberg:view-product', function (/** @type {any} */ e) {
    fireViewProduct(e.detail || {});
  });

  // ── atomberg:check-delivery → check_delivery_time ─────────────────────────
  // Dispatched once a check settles — by atomberg-delivery-check.js on the PDP and
  // atomberg-cart-edd.js in the cart. The PDP sends one product's strings; the cart
  // sends parallel arrays across its items, the same shape view_cart uses.
  document.addEventListener('atomberg:check-delivery', function (/** @type {any} */ e) {
    var d = e.detail || {};
    var pincode = d.pincode || '';
    deferTrack(function () {
      var m = moe();
      if (!m) return;
      // 'pinocode' typo is intentional — matches the MoEngage event spec.
      m.track('check_delivery_time', {
        pinocode: pincode,
        page_type: d.page_type || '',
        // 'user' is the Check button; every other refetch reports as 'background'
        trigger: d.trigger || 'background',
        is_serviceable: d.is_serviceable === true,
        error: d.error === true,
        product_name: d.product_name || '',
        product_variant: d.product_variant || '',
        // custom.variant_title — blank on variants where the metafield is unset
        variant_title: d.variant_title || '',
      });
    });
  });

  // ── atomberg:login → login + User Logged In ───────────────────────────────
  // atomberg-moengage-init.liquid dispatches this once per session when a
  // customer is present (guarded by sessionStorage so it fires at most once).
  document.addEventListener('atomberg:login', function (/** @type {any} */ e) {
    var m = moe();
    if (!m) return;
    var d = e.detail || {};
    m.track('login', {
      phone_number: d.phone_number || '',
      email_id: d.email_id || '',
      type_of_device: d.type_of_device || '',
    });
    m.track('User Logged In', { Mode: 'Mobile' });
  });

  // ── atomberg:support-otp-changed / atomberg:warranty-otp-skipped ──────────
  // Identity merge only, same reasoning as Magic Checkout's user_data: no
  // login/User Logged In event here (verifying a phone on a support/warranty
  // form isn't an account login), and skipped for already-logged-in Shopify
  // customers so a number typed into the form (which may not belong to the
  // account owner — filing on someone else's behalf, etc.) can't reassign the
  // account identity already set server-side.
  //
  // atomberg-support-otp.js dispatches support-otp-changed on both the
  // Support page and the Warranty wizard (shared OTP component) — only the
  // verified:true case is an identity signal, verified:false (the Change
  // link resetting state) is not. atomberg-warranty-wizard.js dispatches
  // warranty-otp-skipped from validateScreen on every successful attempt to
  // leave the phone screen while skipped (not just the first skip click),
  // so re-clicking Next without changing anything can fire this repeatedly —
  // deduped against the last-set mobile so that doesn't spam setUser.
  document.addEventListener('atomberg:support-otp-changed', function (/** @type {any} */ e) {
    if (PAGE_DATA.is_logged_in) return;
    var d = e.detail || {};
    if (!d.verified) return;
    var m = moe();
    if (!m) return;
    var mobile = d.mobile || '';
    if (!mobile) return;
    m.setUser({ id: mobile, mobile: mobile });
  });

  var lastWarrantySkipMobile = '';
  document.addEventListener('atomberg:warranty-otp-skipped', function (/** @type {any} */ e) {
    if (PAGE_DATA.is_logged_in) return;
    var mobile = (e.detail || {}).mobile || '';
    if (!mobile || mobile === lastWarrantySkipMobile) return;
    var m = moe();
    if (!m) return;
    lastWarrantySkipMobile = mobile;
    m.setUser({ id: mobile, mobile: mobile });
  });

  // ── atomberg:searched → searched ──────────────────────────────────────────
  // atomberg-moengage-init.liquid dispatches this on search form submit.
  document.addEventListener('atomberg:searched', function (/** @type {any} */ e) {
    var m = moe();
    if (!m) return;
    var keyword = (e.detail || {}).keyword || '';
    if (keyword) m.track('searched', { keyword: keyword });
  });

  // ── atomberg:page-view → view_support_page / view_warranty_page / view_store_locator_page
  // atomberg-moengage-init.liquid dispatches this for known static page handles.
  document.addEventListener('atomberg:page-view', function (/** @type {any} */ e) {
    var m = moe();
    if (!m) return;
    var d = e.detail || {};
    var eventName = d.event || '';
    if (!eventName) return;
    m.track(eventName, d.payload || {});
  });

  // ── atomberg:social-click → whatsApp_icon_click / call_icon_click ─────────
  // atomberg-floating-actions.liquid dispatches this on FAB link click.
  document.addEventListener('atomberg:social-click', function (/** @type {any} */ e) {
    var m = moe();
    if (!m) return;
    var d = e.detail || {};
    var eventName = d.type === 'whatsapp' ? 'whatsApp_icon_click' : 'call_icon_click';
    m.track(eventName, { phone_number: d.phone_number || '' });
  });

  // ── atomberg:warranty-scan-opened / -succeeded → scan_opened / scan_succeeded
  // atomberg-warranty-scan.js dispatches these for Register Warranty's
  // barcode/QR camera scanner. detection_method distinguishes native
  // BarcodeDetector vs the zxing-wasm fallback, since success rate differs by
  // path — the whole point of tracking this is measuring scan success ratio.
  document.addEventListener('atomberg:warranty-scan-opened', function (/** @type {any} */ e) {
    var m = moe();
    if (!m) return;
    var d = e.detail || {};
    m.track('scan_opened', {
      detection_method: d.detection_method || '',
      device_type: d.device_type || '',
      product_position: d.product_position || 1,
      total_products_in_form: d.total_products_in_form || 1,
      page: w.location.pathname,
      event_time: new Date().toISOString(),
    });
  });

  document.addEventListener('atomberg:warranty-scan-succeeded', function (/** @type {any} */ e) {
    var m = moe();
    if (!m) return;
    var d = e.detail || {};
    m.track('scan_succeeded', {
      detection_method: d.detection_method || '',
      device_type: d.device_type || '',
      barcode_format: d.barcode_format || '',
      duration_ms: d.duration_ms || 0,
      serial_number: d.serial_number || '',
      product_position: d.product_position || 1,
      total_products_in_form: d.total_products_in_form || 1,
      page: w.location.pathname,
      event_time: new Date().toISOString(),
    });
  });

  // ── atomberg:warranty-event → Warranty Registration tracking plan ───────
  // Generic pipe for the 13 non-scan events atomberg-warranty-wizard.js and
  // atomberg-support-repeatable.js fire (fireWarrantyEvent/fireWarranty) —
  // one listener instead of 13 named ones. event_date_time/page_url are
  // attached here rather than at each dispatch site.
  document.addEventListener('atomberg:warranty-event', function (/** @type {any} */ e) {
    var m = moe();
    if (!m) return;
    var d = e.detail || {};
    if (!d.name) return;
    m.track(
      d.name,
      Object.assign(
        {
          event_date_time: new Date().toISOString(),
          page_url: w.location.href,
        },
        d.payload || {}
      )
    );
  });

  // ── Checkout button → buy_now + begin_checkout ────────────────────────────
  // Capture phase so we run before the inline onclick navigates to checkout.
  document.addEventListener(
    'click',
    function (e) {
      var t = /** @type {HTMLElement} */ (e.target);
      var btn = t.closest(
        '#checkout, [onclick*="openRzpCheckout"], [onclick*="onBuyNowClickMagic"]'
      );
      if (btn) fireCheckoutEvents();
    },
    true
  );

  // ── Magic Checkout event bridge ────────────────────────────────────────────
  // Magic Checkout does NOT dispatch window CustomEvents for its funnel steps
  // (confirmed by Razorpay POC + a live capture that showed nothing but
  // magic.checkout_data firing) — the listeners this section used to have
  // (otp_submitted, coupons_applied_success, payment_method_selected,
  // payment_successful, etc.) never fired. Its real transport is
  // 'mx-analytics' on the Razorpay SDK instance it creates internally, which
  // we can't attach to directly — atomberg-magic-checkout-analytics.js
  // intercepts window.Razorpay to get that instance and re-dispatches each
  // event onto window as magic:<event-name> (Razorpay's own names,
  // unmodified), plus magic:complete from window.onCompleteMagiCheckout for
  // the purchase signal. atomberg-gtm-datalayer.js consumes the same events
  // for GTM — see that file for the underlying data-source notes (lineItems
  // field names unconfirmed from static analysis, order.shopify_cart shape
  // uncertain, etc.). make_payment_button (previously mapped from the dead
  // pay_now_clicked) has no known live trigger and was removed rather than
  // left as dead code.
  function watchMagicEvents() {
    /** @param {any} e @returns {Record<string, any>} */
    function detail(e) {
      return (e && e.detail) || {};
    }

    /**
     * Map a raw mx-analytics lineItems entry to MoEngage's flat parallel-array
     * shape (mirrors normalize()/arraysPayload() above, adapted for Magic's
     * field names instead of /cart.js's).
     * @param {any} item
     * @returns {Record<string, any>}
     */
    function magicNormalize(item) {
      return {
        category_name: item.type || item.product_type || item.item_category || '',
        product_name: item.title || item.item_name || item.name || '',
        product_colour: item.variant_id || item.item_variant || '',
        product_id: item.sku || item.item_id || String(item.product_id || item.variant_id || ''),
        image_url: item.image_url || item.image || '',
        quantity: item.quantity || 1,
        price: parseFloat(item.price || item.final_price || 0) || 0,
        regular_price: parseFloat(item.original_price || item.mrp || item.price || 0) || 0,
      };
    }

    /**
     * @param {any[]} lineItems - raw mx-analytics lineItems or order cart line_items
     * @param {string[]} keys
     * @returns {Record<string, any[]>}
     */
    function magicArraysPayload(lineItems, keys) {
      /** @type {Record<string, any[]>} */
      var out = {};
      keys.forEach(function (k) {
        out[k] = [];
      });
      (lineItems || []).forEach(function (/** @type {any} */ item) {
        var n = magicNormalize(item);
        keys.forEach(function (k) {
          var arr = out[k];
          if (arr) arr.push(n[k]);
        });
      });
      return out;
    }

    var ARRAY_KEYS = [
      'category_name',
      'product_name',
      'quantity',
      'product_colour',
      'product_id',
      'price',
      'regular_price',
      'image_url',
    ];

    // ── otp_submitted ────────────────────────────────────────────────────────
    // mx-analytics only confirms one OTP-related event ('otp_submitted') —
    // matches the pre-existing MoEngage naming exactly (GTM tracks the same
    // transport as 'otp_initiated' instead; a pre-existing inconsistency
    // between the two systems, left as-is rather than unified here).
    w.addEventListener('magic:otp_submitted', function (/** @type {any} */ e) {
      var d = detail(e);
      var m = moe();
      if (!m) return;
      m.track('otp_submitted', {
        isScriptCouponApplied: false,
        phone: d.phone || d.mobile || '',
        email: d.email || '',
        state: d.state || '',
        city: d.city || '',
        lineItems: JSON.stringify(d.lineItems || []),
        first_name: d.first_name || d.firstName || '',
        last_name: d.last_name || d.lastName || '',
        otp_verified: true,
        totalAmount: (d.totalAmount || 0) / 100,
        latestTotal: (d.latestTotal || 0) / 100,
        couponDiscountValue: (d.couponDiscountValue || 0) / 100,
        currency: 'INR',
        checkout_page: 'magic_checkout',
      });
    });

    // ── coupon_applied ─────────────────────────────────────────────────────
    w.addEventListener('magic:coupon_applied', function (/** @type {any} */ e) {
      var d = detail(e);
      var m = moe();
      if (!m) return;
      var before = (d.totalAmount || 0) / 100;
      var discount = (d.couponDiscountValue || 0) / 100;
      m.track('coupon_applied', {
        paymentMode: 'magic_checkout',
        cart_value_before_discount: before,
        cart_value_after_discount: before - discount,
        coupon_code: d.appliedCouponCode || d.couponCode || '',
        discount_amount: discount,
        checkout_page: 'magic_checkout',
      });
    });

    // ── coupon_failed ──────────────────────────────────────────────────────
    w.addEventListener('magic:coupon_failed', function (/** @type {any} */ e) {
      var d = detail(e);
      var m = moe();
      if (!m) return;
      m.track('coupon_failed', {
        paymentMode: 'magic_checkout',
        coupon_code: d.couponCode || d.appliedCouponCode || '',
        message: d.errorMsg || d.message || '',
        page_url: w.location.href,
        checkout_page: 'magic_checkout',
      });
    });

    // ── checkout_details_submitted + Checkout_User_Info_Submitted ──────────
    // mx-analytics' shipping_selected carries lineItems directly — falls back
    // to cartCache (populated at begin_checkout) only if that's empty.
    w.addEventListener('magic:shipping_selected', function (/** @type {any} */ e) {
      var d = detail(e);
      var m = moe();
      if (!m) return;
      var arrays = (d.lineItems || []).length
        ? magicArraysPayload(d.lineItems, ARRAY_KEYS)
        : {
            category_name: cartCache.category_name || [],
            product_name: cartCache.product_name || [],
            quantity: cartCache.quantity || [],
            product_colour: cartCache.product_colour || [],
            product_id: cartCache.product_id || [],
            price: cartCache.price || [],
            image_url: cartCache.image_url || [],
          };
      var customerName = (
        (d.first_name || d.firstName || '') +
        ' ' +
        (d.last_name || d.lastName || '')
      ).trim();
      var payload = {
        category_name: arrays.category_name,
        product_name: arrays.product_name,
        product_quantity: arrays.quantity,
        quantity: arrays.quantity,
        product_colour: arrays.product_colour,
        product_id: arrays.product_id,
        size: [],
        price: arrays.price,
        total_price: (d.totalAmount || 0) / 100 || cartCache.total_price || 0,
        image_url: arrays.image_url,
        name: customerName,
        email_id: d.email || '',
        phone_number: d.phone || d.phone_number || '',
        city: d.city || '',
        pincode: d.zipcode || d.pincode || d.zip || '',
        checkout_page: 'magic_checkout',
      };
      m.track('checkout_details_submitted', payload);
      m.track('Checkout_User_Info_Submitted', payload);
    });

    // ── payment_initiated ──────────────────────────────────────────────────
    w.addEventListener('magic:payment_initiated', function (/** @type {any} */ e) {
      var d = detail(e);
      var m = moe();
      if (!m) return;
      var items = (d.lineItems || []).map(magicNormalize);
      var totalQty = items.reduce(function (/** @type {number} */ s, /** @type {any} */ it) {
        return s + (it.quantity || 0);
      }, 0);
      m.track('payment_initiated', {
        paymentMode: 'magic_checkout',
        ecommerce: JSON.stringify({
          value: (d.totalAmount || 0) / 100,
          currency: 'INR',
          discount: (d.couponDiscountValue || 0) / 100,
          quanitity: totalQty, // intentional typo — matches GTM variable key
          items: items,
          payment_type: d.paymentMode || d.paymentMethod || 'razorpay',
          email_id: d.email || '',
          first_name: d.first_name || d.firstName || '',
          last_name: d.last_name || d.lastName || '',
          phone_number: d.phone || d.phone_number || '',
          address: d.address || d.line1 || '',
          zip_code: d.zipcode || d.zip || '',
          city: d.city || '',
          region: d.state || '',
          coupen: d.appliedCouponCode || '', // intentional typo — matches GTM variable key
        }),
        checkout_page: 'magic_checkout',
      });
    });

    // ── payment_failure ────────────────────────────────────────────────────
    // mx-analytics' payment_failed payload isn't confirmed to carry lineItems
    // — falls back to cartCache for the product arrays.
    w.addEventListener('magic:payment_failed', function (/** @type {any} */ e) {
      var d = detail(e);
      var m = moe();
      if (!m) return;
      var utms = readUtms();
      m.track(
        'payment_failure',
        Object.assign(
          {
            category_name: cartCache.category_name || [],
            product_name: cartCache.product_name || [],
            quantity: cartCache.quantity || [],
            sale_price: cartCache.price || [],
            regular_price: cartCache.regular_price || [],
            payment_method: d.paymentMode || d.payment_mode || d.method || '',
            name: d.first_name || d.name || '',
            email_id: d.email || '',
            phone_number: d.phone_number || d.phone || '',
            coupon_code_applied: cartCache.coupon_code ? 'Yes' : 'No',
            coupon_code_amount: '',
            coupon_code_name: cartCache.coupon_code || '',
            order_amount: (d.totalAmount || 0) / 100,
            checkout_page: 'magic_checkout',
          },
          utms
        )
      );
    });

    // ── payment_successful — via window.onCompleteMagiCheckout ─────────────
    // magic:complete is bridged from Razorpay's own order-completion callback
    // (see atomberg-magic-checkout-analytics.js) — the only confirmed
    // post-payment signal. order carries customer_details, shipping_fee,
    // total_tax, promotions, and cart line items directly; cartCache is only
    // a fallback for the product arrays if the order object lacks them.
    // KNOWN GAP: order_id below is Razorpay's own order id (order_XXXXXXXXX),
    // not Shopify's order name (#1234) — Shopify's order doesn't exist yet at
    // the moment this callback fires.
    w.addEventListener('magic:complete', function (/** @type {any} */ e) {
      var d = detail(e);
      var m = moe();
      if (!m) return;
      var order = d.order || {};
      var cust = order.customer_details || {};
      var cartSource = order.shopify_cart || order.customer_cart || {};
      var rawItems = cartSource.line_items || cartSource.items || [];
      var arrays = rawItems.length
        ? magicArraysPayload(rawItems, ARRAY_KEYS)
        : {
            category_name: cartCache.category_name || [],
            product_name: cartCache.product_name || [],
            quantity: cartCache.quantity || [],
            price: cartCache.price || [],
            regular_price: cartCache.regular_price || [],
          };
      var utms = readUtms();
      var customerName = (
        (cust.first_name || cust.firstName || '') +
        ' ' +
        (cust.last_name || cust.lastName || '')
      ).trim();
      var coupon =
        (order.promotions || [])
          .map(function (/** @type {any} */ p) {
            return p.code || '';
          })
          .filter(Boolean)
          .join('|') ||
        cartCache.coupon_code ||
        '';
      m.track(
        'payment_successful',
        Object.assign(
          {
            category_name: arrays.category_name,
            product_name: arrays.product_name,
            quantity: arrays.quantity,
            sale_price: arrays.price,
            regular_price: arrays.regular_price,
            payment_method: order.payment_method || '',
            name: customerName,
            email_id: cust.email || '',
            phone_number: cust.contact || cust.phone || '',
            coupon_code_applied: coupon ? 'Yes' : 'No',
            coupon_code_amount: '',
            coupon_code_name: coupon,
            order_amount: (d.totalAmount || 0) / 100,
            order_id: d.orderId || order.order_id || '',
          },
          utms
        )
      );
    });

    // ── user_data → identity merge (Magic Checkout) ─────────────────────────
    // Confirmed live: fires repeatedly across checkout screens (source_screen
    // distinguishes them — e.g. "checkout_init") with the customer's full
    // contact/address/cart snapshot each time, at the top level of the
    // payload (not nested). Replaces an earlier login_success-based attempt
    // that had two bugs: its identity fields are nested under `params`
    // (login_success's real shape is
    // {event, category:"rzp_login", params:{contact, email, method}} — no
    // top-level phone/contact at all, which is why setUser never fired), and
    // it doesn't carry first_name/last_name anyway. user_data has all of it
    // and is confirmed present whether or not a fresh OTP login just
    // happened, so it's the more robust source. Deduped against the
    // last-seen identity so repeat firings with unchanged data don't
    // re-push to MoEngage on every screen transition.
    //
    // Skipped entirely for already-logged-in Shopify customers: MoEngage's
    // add_unique_user_id switches its active identified user on every call,
    // it doesn't just patch attributes onto the existing one. The checkout's
    // contact details can legitimately differ from the account owner's (gift
    // orders, shared devices, a different delivery contact) — letting
    // user_data call setUser in that case would silently reassign the rest
    // of the session's events to a different user record than the one
    // atomberg-moengage-init.liquid already identified server-side. The
    // account identity, once established, wins.
    var lastUserDataSignature = '';
    w.addEventListener('magic:user_data', function (/** @type {any} */ e) {
      var d = detail(e);
      var m = moe();
      if (!m) return;
      var phone = d.phone || '';
      var email = d.email || '';
      var firstName = d.first_name || '';
      var lastName = d.last_name || '';
      if (!phone && !email) return;

      var signature = [phone, email, firstName, lastName].join('|');
      if (signature === lastUserDataSignature) return;
      lastUserDataSignature = signature;

      m.setUser({
        id: phone || email,
        mobile: phone,
        firstName: firstName,
        lastName: lastName,
        email: email,
      });
    });

    // ── login_failed ─────────────────────────────────────────────────────
    // Diagnostic funnel event, not identity — kept independent of user_data.
    // login_success's confirmed real shape nests fields under `params`
    // (see above); login_failed likely mirrors that same
    // {category, params: {...}} structure as a sibling event, but this
    // specific event's shape isn't confirmed live yet — reads both the
    // nested and flat paths defensively until it is.
    w.addEventListener('magic:login_failed', function (/** @type {any} */ e) {
      var d = detail(e);
      var m = moe();
      if (!m) return;
      var params = d.params || {};
      m.track('login_failed', {
        reason: params.reason || params.message || params.error || d.reason || d.message || '',
        checkout_page: 'magic_checkout',
      });
    });
  }

  // ── Join_Live_Demo_Btn_Click ───────────────────────────────────────────────
  // Mirrors the GTM handler. On PDP, product data comes from PAGE_DATA.product.
  // On collection pages PAGE_DATA.product is null — fall back to data-product-*
  // attributes stamped on the element by atomberg-product-card.liquid.
  function watchJoinDemo() {
    var utms = readUtms();
    document.addEventListener('click', function (/** @type {MouseEvent} */ e) {
      var t = /** @type {HTMLElement} */ (e.target);
      var btn = /** @type {HTMLElement|null} */ (
        t.closest ? t.closest('[data-join-live-demo]') : null
      );
      if (!btn) return;
      var demoBtn = btn;
      deferTrack(function () {
        var m = moe();
        if (!m) return;
        var sku = PAGE_DATA.product
          ? PAGE_DATA.product.item_id
          : demoBtn.getAttribute('data-product-sku') || '';
        var name = PAGE_DATA.product
          ? PAGE_DATA.product.item_name
          : demoBtn.getAttribute('data-product-name') || '';
        var productType = demoBtn.getAttribute('data-join-live-demo') || '';
        var payload = Object.assign(
          {
            id: sku,
            page_section: demoBtn.getAttribute('data-page-section') || '',
            name: name,
            phone_number: PAGE_DATA.customer ? PAGE_DATA.customer.phone || '' : '',
            page: w.location.href,
          },
          utms
        );
        m.track('Join_Live_Demo_Btn_Click', payload);
        if (productType) m.track('Join_Live_Demo_' + productType, payload);
      });
    });
  }

  // ── start ─────────────────────────────────────────────────────────────────
  function start() {
    // initOptionNames must run before any atomberg:pdp-variant-change fires.
    if (PAGE_DATA.template === 'product') initOptionNames();
    if (PAGE_DATA.template === 'collection') fireViewCategory();
    if (onCartPage()) fireViewCart();
    watchDrawer();
    initCartDiff();
    watchMagicEvents();
    watchJoinDemo();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
